/**
 * Admin > Orders tab.
 *
 * Extracted from AdminPage.jsx (Feb 2026 refactor).
 * Owns its own filter state; parent supplies data + callback handlers.
 */
import React, { useState } from 'react';
import { ClipboardList, Download, Wallet, Trash2 } from 'lucide-react';
import { resolveImage } from './ImageUpload';

const ORDER_STATUSES = ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'];
const STATUS_COLORS = {
  pending: 'bg-neutral-100 text-neutral-700',
  confirmed: 'bg-blue-100 text-blue-700',
  shipped: 'bg-orange-100 text-orange-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const emptyFilters = {
  status_filter: '', user_type: '', email: '',
  date_from: '', date_to: '', min_total: '', max_total: '', payment_method: '',
};

const OrdersAdmin = ({ orders, onStatusChange, onFilter, onExport, onDelete }) => {
  const [filters, setFilters] = useState(emptyFilters);
  const set = (k) => (e) => setFilters(prev => ({ ...prev, [k]: e.target.value }));

  return (
    <div>
      <div className="bg-white border border-neutral-200 rounded-sm p-4 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="font-bold text-[#12263A] flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Orders ({orders.length})</h3>
          <div className="flex gap-2">
            <button onClick={() => { setFilters(emptyFilters); onFilter({}); }} className="text-xs border border-neutral-300 hover:border-[#F46A25] hover:text-[#F46A25] font-semibold px-3 py-1.5 rounded-sm">Clear</button>
            <button onClick={() => onFilter(filters)} className="text-xs bg-[#12263A] hover:bg-[#F46A25] text-white font-bold px-3 py-1.5 rounded-sm">Apply filters</button>
            <button onClick={() => onExport(filters)} className="text-xs bg-green-600 hover:bg-green-700 text-white font-bold px-3 py-1.5 rounded-sm flex items-center gap-1"><Download className="w-3 h-3" /> Export Excel</button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 text-xs">
          <div>
            <label className="block font-semibold text-[#12263A] mb-1">Status</label>
            <select value={filters.status_filter} onChange={set('status_filter')} className="w-full border border-neutral-300 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#F46A25]">
              <option value="">All</option>
              {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-semibold text-[#12263A] mb-1">Payment</label>
            <select value={filters.payment_method} onChange={set('payment_method')} className="w-full border border-neutral-300 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#F46A25]" data-testid="filter-payment-method">
              <option value="">All</option>
              <option value="instant">Instant</option>
              <option value="credit">Credit</option>
            </select>
          </div>
          <div>
            <label className="block font-semibold text-[#12263A] mb-1">Customer type</label>
            <select value={filters.user_type} onChange={set('user_type')} className="w-full border border-neutral-300 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#F46A25]">
              <option value="">All</option>
              <option value="end_user">End user</option>
              <option value="builder">Builder</option>
              <option value="retailer">Retailer</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block font-semibold text-[#12263A] mb-1">Email contains</label>
            <input value={filters.email} onChange={set('email')} placeholder="e.g. @gmail.com" className="w-full border border-neutral-300 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#F46A25]" />
          </div>
          <div>
            <label className="block font-semibold text-[#12263A] mb-1">From date</label>
            <input type="date" value={filters.date_from} onChange={set('date_from')} className="w-full border border-neutral-300 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#F46A25]" />
          </div>
          <div>
            <label className="block font-semibold text-[#12263A] mb-1">To date</label>
            <input type="date" value={filters.date_to} onChange={set('date_to')} className="w-full border border-neutral-300 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#F46A25]" />
          </div>
          <div>
            <label className="block font-semibold text-[#12263A] mb-1">Min £</label>
            <input type="number" value={filters.min_total} onChange={set('min_total')} className="w-full border border-neutral-300 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#F46A25]" />
          </div>
          <div>
            <label className="block font-semibold text-[#12263A] mb-1">Max £</label>
            <input type="number" value={filters.max_total} onChange={set('max_total')} className="w-full border border-neutral-300 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#F46A25]" />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {orders.length === 0 && <div className="bg-white border border-neutral-200 rounded-sm p-10 text-center text-neutral-600">No orders match your filters</div>}
        {orders.map(o => (
          <div key={o.id} className="bg-white border border-neutral-200 rounded-sm p-4">
            <div className="flex flex-wrap items-center gap-4 justify-between mb-3 pb-3 border-b border-neutral-100">
              <div>
                <div className="text-xs text-neutral-500">Order</div>
                <div className="font-mono font-bold text-[#12263A]">#{o.id.slice(0, 8)}</div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">Customer</div>
                <div className="text-sm font-semibold text-[#12263A]">{o.user_email}</div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">Date</div>
                <div className="text-sm text-[#12263A]">{new Date(o.created_at).toLocaleString('en-GB')}</div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">Total {o.total_inc_vat != null ? <span className="text-[9px] font-normal">(inc VAT)</span> : <span className="text-[9px] font-normal">(ex VAT)</span>}</div>
                <div className="text-sm font-bold text-[#12263A]">£{Number(o.total_inc_vat != null ? o.total_inc_vat : o.total).toFixed(2)}</div>
              </div>
              <div>
                <div className="text-xs text-neutral-500">Payment</div>
                {o.payment_method === 'credit' ? (
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm bg-[#F46A25] text-white inline-flex items-center gap-1"><Wallet className="w-3 h-3" /> Credit</span>
                ) : (
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm bg-neutral-200 text-[#12263A]">Instant</span>
                )}
              </div>
              <div>
                <div className="text-xs text-neutral-500 mb-1">Status</div>
                <select value={o.status} onChange={e => onStatusChange(o.id, e.target.value)} className={`text-xs font-bold uppercase px-2 py-1 rounded-sm border-0 ${STATUS_COLORS[o.status] || ''}`}>
                  {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {onDelete && (
                <div>
                  <div className="text-xs text-neutral-500 mb-1 invisible">.</div>
                  <button
                    onClick={() => {
                      if (window.confirm(`Permanently delete order #${o.id.slice(0, 8)}? This cannot be undone.`)) onDelete(o.id);
                    }}
                    data-testid={`delete-order-${o.id}`}
                    className="text-xs text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 font-bold px-2 py-1 rounded-sm flex items-center gap-1 transition-colors"
                    title="Delete this order"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-4 text-sm">
              <div>
                <div className="text-xs font-bold text-[#12263A] mb-2">Items ({o.items.length})</div>
                <div className="space-y-1">
                  {o.items.map((i, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <img src={resolveImage(i.image)} alt="" className="w-10 h-10 object-cover rounded-sm border border-neutral-200" />
                      <div className="flex-1 text-xs">
                        <div className="font-semibold">{i.name}</div>
                        <div className="text-neutral-600">Qty {i.qty} × £{i.price.toFixed(2)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-bold text-[#12263A] mb-2">Delivery address</div>
                <div className="text-xs text-neutral-700 space-y-0.5">
                  <div>{o.address?.name}</div>
                  <div>{o.address?.line1}</div>
                  {o.address?.line2 && <div>{o.address.line2}</div>}
                  <div>{o.address?.city}, {o.address?.postcode}</div>
                  {o.address?.phone && <div>☎ {o.address.phone}</div>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrdersAdmin;
